import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { AlertTriangle, ArrowDownCircle, ArrowUpCircle, Bookmark, Loader2, ShoppingCart, Store, Trash2, Truck } from 'lucide-react';
import { ShopChrome, SignInPrompt } from '@/components/shop/ShopChrome';
import PriceSummary from '@/components/shop/PriceSummary';
import { QtyStepper } from '@/components/shop/ShopUI';
import { EmptyState, Img, Shimmer } from '@/components/ui';
import { getCart, removeCartItem, updateCartItem } from '@/api/shop';
import { useAuth } from '@/context/AuthContext';
import { syncCartCount } from '@/lib/shopStore';
import { friendlyError } from '@/lib/apiError';
import { rupees } from '@/lib/shop';

const PROBLEM_TEXT = {
  out_of_stock: 'Out of stock — remove it or save it for later',
  unavailable: 'No longer sold — remove it to continue',
  qty_reduced: 'Quantity reduced to what is left in stock',
};

// Saved-for-later lines are not being bought, so "remove it to continue" is wrong there.
const SAVED_PROBLEM_TEXT = {
  out_of_stock: "Out of stock right now — it stays here; move it to your cart when it's back",
  unavailable: 'No longer sold — remove it from your list',
  qty_reduced: (item) => `Only ${item.maxQty} left in stock`,
};

const problemText = (item, saved) => {
  const t = (saved ? SAVED_PROBLEM_TEXT : PROBLEM_TEXT)[item.problem];
  return typeof t === 'function' ? t(item) : t;
};

const CartLine = ({ item, busy, onQty, onSave, onRemove, onMove, saved }) => {
  const off = item.discountPercent;
  // `busy` is the action running on this line ('qty' | 'save' | 'move' | 'remove'), or null.
  const spin = (action, Icon) => (busy === action ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Icon className="h-3.5 w-3.5" />);
  const cantMove = item.problem === 'out_of_stock' || item.problem === 'unavailable';
  return (
    <div className={`flex gap-3 p-4 sm:gap-4 sm:p-5 ${busy ? 'opacity-60' : ''}`} aria-busy={Boolean(busy)}>
      <Link to={`/shop/p/${item.productSlug}?variant=${item.variantId}`} className="lk-focus shrink-0 rounded-xl">
        <Img src={item.image} alt={item.title} className="h-28 w-[88px] rounded-xl sm:h-32 sm:w-[104px]" />
      </Link>
      <div className="min-w-0 flex-1">
        <Link to={`/shop/p/${item.productSlug}?variant=${item.variantId}`} className="lk-focus line-clamp-2 rounded text-sm font-medium text-ink-900 hover:underline">
          {item.title}
        </Link>
        {item.variantLabel && <p className="mt-0.5 text-xs text-ink-500">{item.variantLabel}</p>}
        <p className="mt-0.5 text-xs text-ink-500">Seller: {item.sellerName}</p>

        <div className="tabular mt-2 flex flex-wrap items-baseline gap-x-2">
          {item.mrp > item.price && <span className="text-xs text-ink-400 line-through">{rupees(item.mrp * item.qty)}</span>}
          <span className="text-base font-bold text-ink-900">{rupees(item.lineTotal)}</span>
          {off > 0 && <span className="text-xs font-semibold text-success">{off}% off</span>}
        </div>
        {item.qty > 1 && <p className="tabular text-[11px] text-ink-500">{rupees(item.price)} each</p>}

        {item.priceDrop > 0 && (
          <p className="mt-1.5 inline-flex items-center gap-1 rounded-lg bg-success-tint px-2 py-1 text-xs font-semibold text-success">
            <ArrowDownCircle className="h-3.5 w-3.5" /> Price dropped by {rupees(item.priceDrop)} since you added it
          </p>
        )}
        {!saved && item.priceRise > 0 && (
          <p className="mt-1.5 inline-flex items-center gap-1 rounded-lg bg-warning-tint px-2 py-1 text-xs font-semibold text-warning">
            <ArrowUpCircle className="h-3.5 w-3.5" /> Price went up by {rupees(item.priceRise)} since you added it
          </p>
        )}
        {item.problem && (
          <p className={`mt-1.5 flex items-start gap-1 text-xs font-semibold ${item.problem === 'qty_reduced' || saved ? 'text-warning' : 'text-danger'}`}>
            <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" /> {problemText(item, saved)}
          </p>
        )}
        {!saved && item.delivery?.label && !item.problem && (
          <p className="mt-1.5 text-xs text-ink-500">
            Delivery by <span className="font-semibold text-ink-700">{item.delivery.label}</span>
          </p>
        )}

        <div className="mt-3 flex flex-wrap items-center gap-x-1 gap-y-2">
          {!saved && item.problem !== 'out_of_stock' && item.problem !== 'unavailable' && (
            <span className="mr-2">
              <QtyStepper size="sm" value={item.qty} max={Math.max(item.qty, item.maxQty || 1)} onChange={onQty} disabled={Boolean(busy)} />
            </span>
          )}
          {saved ? (
            <button type="button" onClick={onMove} disabled={Boolean(busy) || cantMove} className="lk-focus inline-flex min-h-[36px] items-center gap-1.5 rounded-lg px-2.5 text-xs font-semibold uppercase tracking-wide text-brand-secondary hover:bg-brand-tint disabled:opacity-40">
              {spin('move', ShoppingCart)} Move to cart
            </button>
          ) : (
            <button type="button" onClick={onSave} disabled={Boolean(busy)} className="lk-focus inline-flex min-h-[36px] items-center gap-1.5 rounded-lg px-2.5 text-xs font-semibold uppercase tracking-wide text-ink-700 hover:bg-surface-muted">
              {spin('save', Bookmark)} Save for later
            </button>
          )}
          <button type="button" onClick={onRemove} disabled={Boolean(busy)} className="lk-focus inline-flex min-h-[36px] items-center gap-1.5 rounded-lg px-2.5 text-xs font-semibold uppercase tracking-wide text-ink-700 hover:bg-danger-tint hover:text-danger">
            {spin('remove', Trash2)} Remove
          </button>
          {busy === 'qty' && <Loader2 className="h-4 w-4 animate-spin text-ink-400" aria-label="Updating" />}
        </div>
      </div>
    </div>
  );
};

const CartPage = () => {
  const { user, openAuthModal } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [cart, setCart] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null); // { id, action }
  const [reloading, setReloading] = useState(false);
  // Checkout sends people back here with the reason ("only 2 left").
  const [notice, setNotice] = useState(location.state?.notice || null);
  // Lines whose quantity the cart lowered to the stock left. GET /shop/cart
  // saves the lower quantity and reports the line once, so remember them for
  // as long as the page is open (a later reload no longer lists them).
  const [adjusted, setAdjusted] = useState([]);

  // Drop the router state once read, so a refresh does not replay an old notice.
  useEffect(() => {
    if (location.state?.notice) navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const take = useCallback((view) => {
    setCart(view);
    // Keep the header/bar badge in step with what this page shows.
    syncCartCount(view);
    if (view?.adjusted?.length) {
      setAdjusted((prev) => {
        const byId = new Map(prev.map((l) => [l.cartItemId, l]));
        view.adjusted.forEach((l) => byId.set(l.cartItemId, l));
        return [...byId.values()];
      });
    }
  }, []);

  const load = useCallback(() => {
    setError(null);
    setReloading(true);
    return getCart()
      .then(take)
      .catch((err) => setError(friendlyError(err, 'Your cart could not be loaded')))
      .finally(() => setReloading(false));
  }, [take]);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  const run = async (id, action, fn, okMsg) => {
    setBusy({ id, action });
    try {
      const view = await fn();
      take(view);
      if (okMsg) toast.success(okMsg, { id: `cart-${id}` });
    } catch (err) {
      toast.error(friendlyError(err, 'Your cart could not be updated'));
      load();
    } finally {
      setBusy(null);
    }
  };

  if (!user) {
    return (
      <ShopChrome>
        {/* After login this page re-renders with the cart: nothing to replay. */}
        <SignInPrompt title="Your cart is waiting" message="Log in to see the items you added and check out." onLogin={openAuthModal} />
      </ShopChrome>
    );
  }

  const groups = cart?.groups || [];
  const attention = cart?.needsAttention || [];
  const saved = cart?.savedForLater || [];
  const empty = cart && !groups.length && !attention.length;
  // Never send someone to checkout while the cart is still settling: checkout
  // re-validates and would only bounce them straight back here.
  const canCheckout = groups.length > 0 && attention.length === 0 && !busy && !reloading;
  // Only the adjusted lines still in the cart (not removed or saved since).
  const liveIds = new Set(groups.flatMap((g) => g.items.map((i) => i.cartItemId)));
  const adjustedLive = adjusted.filter((l) => liveIds.has(l.cartItemId));

  const line = (item, isSaved = false) => (
    <CartLine
      key={item.cartItemId}
      item={item}
      saved={isSaved}
      busy={busy?.id === item.cartItemId ? busy.action : null}
      onQty={(q) => run(item.cartItemId, 'qty', () => updateCartItem(item.cartItemId, { qty: q }))}
      onSave={() => run(item.cartItemId, 'save', () => updateCartItem(item.cartItemId, { savedForLater: true }), 'Saved for later')}
      onMove={() => run(item.cartItemId, 'move', () => updateCartItem(item.cartItemId, { savedForLater: false }), 'Moved to cart')}
      onRemove={() => run(item.cartItemId, 'remove', () => removeCartItem(item.cartItemId), 'Removed from cart')}
    />
  );

  return (
    <ShopChrome>
      <div className="lk-container py-5 sm:py-8">
        <h1 className="text-h2 text-ink-900">
          My cart
          {cart?.summary?.itemCount > 0 && <span className="tabular ml-2 text-base font-medium text-ink-500">({cart.summary.itemCount})</span>}
        </h1>

        {notice && (
          <div className="mt-4 flex items-start gap-3 rounded-2xl border border-warning/30 bg-warning-tint p-4 text-sm text-warning">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
            <p className="flex-1 font-medium">{notice}</p>
            <button type="button" onClick={() => setNotice(null)} className="text-xs font-semibold underline">
              Dismiss
            </button>
          </div>
        )}

        {error ? (
          <EmptyState className="mt-6" icon={ShoppingCart} title="Something went wrong" message={error} action={<button type="button" onClick={load} className="lk-btn-primary">Try again</button>} />
        ) : !cart ? (
          <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_360px]">
            <div className="space-y-4">
              {[0, 1].map((i) => (
                <Shimmer key={i} className="h-44 w-full rounded-2xl" />
              ))}
            </div>
            <Shimmer className="h-72 w-full rounded-2xl" />
          </div>
        ) : (
          <div className="mt-5 grid items-start gap-6 lg:grid-cols-[1fr_360px]">
            <div className="min-w-0 space-y-4">
              {empty && (
                <EmptyState
                  icon={ShoppingCart}
                  title="Your cart is empty"
                  message="Explore the shop and add things you love."
                  action={<Link to="/shop" className="lk-btn-primary">Start shopping</Link>}
                />
              )}

              {adjustedLive.length > 0 && (
                <div className="flex items-start gap-3 rounded-2xl border border-warning/30 bg-warning-tint p-4 text-sm text-warning" role="status">
                  <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold">
                      Stock ran low on {adjustedLive.length} item{adjustedLive.length > 1 ? 's' : ''}, so we lowered the quantity to what is left.
                    </p>
                    <ul className="mt-1 space-y-0.5 text-xs">
                      {adjustedLive.map((l) => (
                        <li key={l.cartItemId} className="line-clamp-2">
                          {l.title}
                          {l.variantLabel ? ` (${l.variantLabel})` : ''} — now {l.qty}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <button type="button" onClick={() => setAdjusted([])} className="text-xs font-semibold underline">
                    Dismiss
                  </button>
                </div>
              )}

              {attention.length > 0 && (
                <section className="overflow-hidden rounded-2xl border border-danger/30 bg-white">
                  <h2 className="flex items-center gap-2 bg-danger-tint px-5 py-3 text-sm font-semibold text-danger">
                    <AlertTriangle className="h-4 w-4" />
                    {attention.length} item{attention.length > 1 ? 's need' : ' needs'} your attention before checkout
                  </h2>
                  <div className="divide-y divide-ink-200/70">{attention.map((i) => line(i))}</div>
                </section>
              )}

              {groups.map((g) => (
                <section key={g.sellerId} className="lk-card overflow-hidden">
                  <header className="flex flex-wrap items-center justify-between gap-2 border-b border-ink-200/70 bg-surface-muted/60 px-5 py-3">
                    <p className="flex items-center gap-2 text-sm font-semibold text-ink-900">
                      <Store className="h-4 w-4 text-ink-500" />
                      {g.sellerName}
                    </p>
                    <p className="tabular text-xs text-ink-500">
                      Delivery: {g.deliveryFee ? <span className="font-semibold text-ink-900">{rupees(g.deliveryFee)}</span> : <span className="font-semibold text-success">Free</span>}
                    </p>
                  </header>
                  {g.freeDeliveryGap > 0 && (
                    <div className="border-b border-ink-200/70 px-5 py-2.5">
                      <p className="flex items-center gap-2 text-xs text-ink-700">
                        <Truck className="h-4 w-4 shrink-0 text-brand-secondary" />
                        Add <span className="font-bold text-ink-900">{rupees(g.freeDeliveryGap)}</span> more from {g.sellerName} for free delivery
                      </p>
                      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-surface-sunken">
                        <div
                          className="h-full rounded-full bg-brand-secondary"
                          style={{ width: `${Math.min(100, (g.subtotal / (g.subtotal + g.freeDeliveryGap)) * 100)}%` }}
                        />
                      </div>
                    </div>
                  )}
                  <div className="divide-y divide-ink-200/70">{g.items.map((i) => line(i))}</div>
                </section>
              ))}

              {saved.length > 0 && (
                <section className="lk-card overflow-hidden">
                  <h2 className="border-b border-ink-200/70 px-5 py-3 text-sm font-semibold text-ink-900">Saved for later ({saved.length})</h2>
                  <div className="divide-y divide-ink-200/70">{saved.map((i) => line(i, true))}</div>
                </section>
              )}
            </div>

            {groups.length > 0 && (
              <aside className="lg:sticky lg:top-24">
                <PriceSummary summary={cart.summary} groups={groups}>
                  <div className="hidden px-5 pb-4 lg:block">
                    <button type="button" disabled={!canCheckout} onClick={() => navigate('/shop/checkout')} className="lk-btn-primary w-full">
                      Place order
                    </button>
                  </div>
                </PriceSummary>
              </aside>
            )}
          </div>
        )}
      </div>

      {groups.length > 0 && (
        <>
          <div className="h-20 lg:hidden" />
          <div className="fixed inset-x-0 bottom-0 z-40 border-t border-ink-200 bg-white px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 shadow-bar lg:hidden">
            <div className="mx-auto flex max-w-3xl items-center gap-3">
              <div className="flex-1">
                {cart.summary.discountOnMrp > 0 && <p className="tabular text-xs text-ink-400 line-through">{rupees(cart.summary.totalMrp)}</p>}
                <p className="tabular text-lg font-bold leading-tight text-ink-900">{rupees(cart.summary.total)}</p>
              </div>
              <button type="button" disabled={!canCheckout} onClick={() => navigate('/shop/checkout')} className="lk-btn-primary px-8">
                Place order
              </button>
            </div>
          </div>
        </>
      )}
    </ShopChrome>
  );
};

export default CartPage;
